import { clamp, isUndefined, omitBy } from 'lodash';
import { ImageApiProvider } from './providers/imageApiProvider';
import { StandardProvider } from './providers/standardProvider';
import type { ImageFitType } from './types/imageFitType';
import type { ImageOptions } from './types/imageOptions';
import type { ImagePosition } from './types/imagePosition';
import type { ImageProvider } from './types/imageProvider';
import type { ImageType } from './types/imageType';
import type { SrcSet } from './types/srcset';

export const image = (url: string): ImageUrl => new ImageUrl(url, {});

export class ImageUrl {
  protected readonly provider: ImageProvider;
  public readonly options: ImageOptions;

  constructor(url: string, options: Partial<ImageOptions>) {
    const imageApiProvider = new ImageApiProvider();

    if (imageApiProvider.supports(url)) {
      this.provider = imageApiProvider;
    } else {
      this.provider = new StandardProvider();
    }

    this.options = this.formatOptions({
      ...this.provider.fromUrl(url as string),
      ...omitBy(options, isUndefined),
    });
  }

  protected formatOptions(options: ImageOptions): ImageOptions {
    options.width = Math.max(options.width, 0) || 0;
    options.height = Math.max(options.height, 0) || 0;
    options.zoom = Math.max(options.zoom, 0);

    options.focal.x = clamp(options.focal.x, 0, 1);
    options.focal.y = clamp(options.focal.y, 0, 1);

    return options;
  }

  public type(type: ImageType): ImageUrl {
    this.options.type = type;

    return this;
  }

  public size(width: number, height: number): ImageUrl {
    this.options.width = width;
    this.options.height = height;

    return this;
  }

  public width(width: number): ImageUrl {
    this.options.width = width;

    return this;
  }

  public height(height: number): ImageUrl {
    this.options.height = height;

    return this;
  }

  public focal(x: number, y: number): ImageUrl {
    this.options.focal = { x, y };

    return this;
  }

  public zoom(zoom: number): ImageUrl {
    this.options.zoom = zoom;

    return this;
  }

  public toString() {
    return this.provider.toUrl(this.formatOptions(this.options));
  }

  public png(): string {
    return this.type('png').toString();
  }

  public jpg(): string {
    return this.jpeg();
  }

  public jpeg(): string {
    return this.type('jpg').toString();
  }

  public webp(): string {
    return this.type('webp').toString();
  }

  public pdf(): string {
    return this.type('pdf').toString();
  }

  public tiff(): string {
    return this.type('tiff').toString();
  }

  public padding(top: number, right: number, bottom: number, left: number): ImageUrl {
    this.options.padding = { top, right, bottom, left };

    return this;
  }

  public mirror(x: boolean, y: boolean): ImageUrl {
    this.options.mirror = { x, y };

    return this;
  }

  public reflect(reflect: number): ImageUrl {
    this.options.reflect = reflect;

    return this;
  }

  public extend(extend: number): ImageUrl {
    this.options.extend = extend;

    return this;
  }

  public rotate(rotate: number): ImageUrl {
    this.options.rotate = rotate;

    return this;
  }

  public fit(fit: ImageFitType): ImageUrl {
    this.options.fit = fit;

    return this;
  }

  public position(position: ImagePosition): ImageUrl {
    this.options.position = position;

    return this;
  }

  public dpi(dpi: number): ImageUrl {
    this.options.dpi = dpi;

    return this;
  }

  public background(background: string): ImageUrl {
    this.options.background = background;

    return this;
  }

  public gif(): string {
    return this.type('gif').toString();
  }

  public srcset(types: ImageType[] = ['webp', 'jpg']): SrcSet[] {
    return types.map((type) => ({
      src: this[type](),
      mime: `image/${type}`,
    }));
  }
}
